import React from 'react'
import { Calendar } from '../types/dimmingProfile.types'
import { DimmingProfileUiStrings } from '../i18n/dimmingProfileI18n'
import { maxSolarElevationDeg } from '../utils/solar'

interface AnnualZenithCurveProps {
  calendar: Calendar
  strings: DimmingProfileUiStrings
}

// Light palette (matches CalendarCurve / DailyStepsCurve so the annual preview
// reads as part of the same set on the app's white cards).
const COLOR_GRID = '#eef2f7'
const COLOR_GRID_MAJOR = '#dde3ec'
const COLOR_AXIS = '#9ca3af'
const COLOR_TEXT = '#6b7280'
const COLOR_LABEL = '#374151'
const COLOR_ELEVATION = '#f59e0b' // amber: max solar elevation curve

// Plot geometry (self-contained; matches CalendarCurve's margin conventions).
const WIDTH = 640
const HEIGHT = 260
const MARGIN_LEFT = 44
const MARGIN_RIGHT = 16
const MARGIN_TOP = 28
const MARGIN_BOTTOM = 36
const PLOT_WIDTH = WIDTH - MARGIN_LEFT - MARGIN_RIGHT
const PLOT_HEIGHT = HEIGHT - MARGIN_TOP - MARGIN_BOTTOM

// One sample per day of the year.
const DAYS = 365
// Degrees axis: 0..MAX_DEG. 70+ covers tropical noon elevations (~90°) only
// partially, so scale to the data's max with a small headroom.
const DEG_TICKS = [0, 10, 20, 30, 40, 50, 60, 70]

// Day-of-year (1..365) at the first of each month (non-leap year), used to place
// the month gridlines/labels on the X axis.
const MONTH_START_DOY = [
  1, 32, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335,
]

const dayToX = (dayOfYear: number): number =>
  MARGIN_LEFT + ((dayOfYear - 1) / (DAYS - 1)) * PLOT_WIDTH

// True when the annual curve cannot be drawn (no latitude → no seasonal model).
function isUnavailable(calendar: Calendar): boolean {
  return !calendar.latitude_deg
}

// Read-only annual preview: the yellow curve is the solar-noon elevation across
// the year (max sun height / min zenith per day), showing the seasonal drift.
//
// NB: an earlier "intensity" staircase sampled the dimming level at the
// solar-noon zenith — but at noon the sun is always high (small zenith, below
// every step threshold), so it read flat at 0% at all non-polar latitudes and
// misled operators. Under this model the dimming level does not vary day-to-day
// (zenith steps are fixed; only sunrise/sunset TIMES shift), so the series
// carried no annual signal and was removed (revue MR !2). See utils/solar.ts
// for the model's limits (simplified declination, no equation of time /
// refraction).
const AnnualZenithCurve: React.FC<AnnualZenithCurveProps> = ({
  calendar,
  strings,
}) => {
  if (isUnavailable(calendar)) {
    return (
      <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 2px 0' }}>
        {strings.annualNoData}
      </p>
    )
  }

  // Use the SIGNED latitude so both hemispheres are correct: the solar helpers
  // compute |lat − δ|, so a southern (negative) latitude naturally peaks in
  // December and a northern one in June. We never hardcode the season — the apex
  // is found from the actual daily samples below.
  const lat = calendar.latitude_deg

  // Sample the solar-noon elevation for each day of the year.
  const elevations: number[] = []
  for (let d = 1; d <= DAYS; d++) {
    elevations.push(maxSolarElevationDeg(lat, d))
  }

  // Find the apex (max elevation) sample to label, like the reference (June for
  // the northern hemisphere, December for the southern).
  let apexIndex = 0
  for (let i = 1; i < elevations.length; i++) {
    if (elevations[i] > elevations[apexIndex]) apexIndex = i
  }
  const apexDay = apexIndex + 1
  const apexValue = elevations[apexIndex]

  // Degrees axis scales to the data's max (min 70° so the reference look holds).
  const maxDeg = Math.max(70, Math.ceil(apexValue / 10) * 10)
  const degToY = (deg: number): number =>
    MARGIN_TOP + (1 - deg / maxDeg) * PLOT_HEIGHT

  // Elevation curve (smooth polyline over daily samples).
  const elevationPath = elevations
    .map((deg, i) => {
      const x = dayToX(i + 1)
      const y = degToY(deg)
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(' ')

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        style={{
          width: '100%',
          height: 'auto',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
        }}
      >
        {/* Title */}
        <text
          x={MARGIN_LEFT}
          y={16}
          textAnchor="start"
          fontSize={13}
          fill={COLOR_LABEL}
        >
          {strings.annualZenithTitle}
        </text>

        {/* Plot border */}
        <rect
          x={MARGIN_LEFT}
          y={MARGIN_TOP}
          width={PLOT_WIDTH}
          height={PLOT_HEIGHT}
          fill="none"
          stroke={COLOR_AXIS}
          strokeWidth={1}
        />

        {/* Horizontal degree grid + labels */}
        {DEG_TICKS.filter((d) => d <= maxDeg).map((deg) => {
          const y = degToY(deg)
          return (
            <g key={`deg-${deg}`}>
              <line
                x1={MARGIN_LEFT}
                y1={y}
                x2={MARGIN_LEFT + PLOT_WIDTH}
                y2={y}
                stroke={deg === 0 ? COLOR_GRID_MAJOR : COLOR_GRID}
                strokeWidth={1}
              />
              <text
                x={MARGIN_LEFT - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill={COLOR_TEXT}
              >
                {deg}
              </text>
            </g>
          )
        })}

        {/* Vertical month grid + labels */}
        {MONTH_START_DOY.map((doy, idx) => {
          const x = dayToX(doy)
          return (
            <g key={`month-${idx}`}>
              <line
                x1={x}
                y1={MARGIN_TOP}
                x2={x}
                y2={MARGIN_TOP + PLOT_HEIGHT}
                stroke={COLOR_GRID_MAJOR}
                strokeWidth={1}
              />
              <text
                x={x}
                y={MARGIN_TOP + PLOT_HEIGHT + 16}
                textAnchor="middle"
                fontSize={10}
                fill={COLOR_TEXT}
              >
                {strings.annualMonths[idx]}
              </text>
            </g>
          )
        })}

        {/* Elevation curve */}
        <path
          d={elevationPath}
          fill="none"
          stroke={COLOR_ELEVATION}
          strokeWidth={2}
        />

        {/* Apex marker + value label (June north / December south) */}
        <circle
          cx={dayToX(apexDay)}
          cy={degToY(apexValue)}
          r={3}
          fill={COLOR_ELEVATION}
        />
        <text
          x={dayToX(apexDay)}
          y={degToY(apexValue) - 8}
          textAnchor="middle"
          fontSize={11}
          fill={COLOR_ELEVATION}
        >
          {`${Math.round(apexValue)}°`}
        </text>

        {/* Legend */}
        <g>
          <line
            x1={MARGIN_LEFT + 8}
            y1={MARGIN_TOP + 12}
            x2={MARGIN_LEFT + 28}
            y2={MARGIN_TOP + 12}
            stroke={COLOR_ELEVATION}
            strokeWidth={2}
          />
          <text
            x={MARGIN_LEFT + 34}
            y={MARGIN_TOP + 16}
            textAnchor="start"
            fontSize={10}
            fill={COLOR_LABEL}
          >
            {strings.annualElevationLabel}
          </text>
        </g>

        {/* Axis captions */}
        <text
          x={MARGIN_LEFT + PLOT_WIDTH / 2}
          y={HEIGHT - 2}
          textAnchor="middle"
          fontSize={12}
          fill={COLOR_TEXT}
        >
          {strings.annualMonthAxis}
        </text>
        <text
          x={12}
          y={MARGIN_TOP + PLOT_HEIGHT / 2}
          textAnchor="middle"
          fontSize={12}
          fill={COLOR_TEXT}
          transform={`rotate(-90 12 ${MARGIN_TOP + PLOT_HEIGHT / 2})`}
        >
          {strings.annualDegAxis}
        </text>
      </svg>
    </div>
  )
}

export default AnnualZenithCurve
